/**
 * EgoConfigForm Component
 *
 * Shared form for configuring an eGO device (used by both Setup and Edit views).
 * Renders siteId input, bayId dropdown, distanceUnit dropdown, and Save button.
 *
 * Requirements: 6.1–6.7, 7.1–7.6
 */

import { useEgoConfigForm } from '../hooks/useEgoConfigForm';
import type { DistanceUnit, EgoDeviceConfig } from '../models/egoModels';
import './EgoConfigForm.css';

export interface EgoConfigFormProps {
  readonly deviceId: string;
  readonly initialConfig?: EgoDeviceConfig;
  readonly mode: 'setup' | 'edit';
  readonly onSaveSuccess?: () => void;
}

const DISTANCE_UNIT_OPTIONS: readonly { readonly value: DistanceUnit; readonly label: string }[] = [
  { value: 'Meters', label: 'Meters' },
  { value: 'Yards', label: 'Yards' },
  { value: 'YardsFeetInches', label: 'YardsFeetInches' },
];

export function EgoConfigForm({ deviceId, initialConfig, onSaveSuccess }: EgoConfigFormProps): JSX.Element {
  const {
    formState,
    setSiteId,
    setBayId,
    setDistanceUnit,
    availableBays,
    isBaysLoading,
    baysError,
    handleSave,
    isSaving,
    saveError,
    saveSuccess,
    validationErrors,
  } = useEgoConfigForm({
    deviceId,
    initialSiteId: initialConfig?.siteId,
    initialBayId: initialConfig?.bayId,
    initialDistanceUnit: initialConfig?.distanceUnit,
    onSaveSuccess,
  });

  const isBayDisabled = !formState.siteId.trim() || isBaysLoading || !!baysError;

  return (
    <form
      className="ego-form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSave();
      }}
    >
      <div className="ego-form__field">
        <label htmlFor="ego-site-id" className="ego-form__label">Site ID</label>
        <input
          id="ego-site-id"
          className="ego-form__input"
          type="text"
          value={formState.siteId}
          onChange={(e) => setSiteId(e.target.value)}
          placeholder="Enter site ID"
        />
        {validationErrors.siteId && (
          <p className="ego-form__error" role="alert">{validationErrors.siteId}</p>
        )}
      </div>

      <div className="ego-form__field">
        <label htmlFor="ego-bay-id" className="ego-form__label">Bay ID</label>
        <select
          id="ego-bay-id"
          className="ego-form__select"
          value={formState.bayId}
          onChange={(e) => setBayId(e.target.value)}
          disabled={isBayDisabled}
        >
          <option value="">Select a bay</option>
          {availableBays.map((bayId) => (
            <option key={bayId} value={bayId}>{bayId}</option>
          ))}
        </select>
        {isBaysLoading && <p className="ego-form__hint">Loading bays…</p>}
        {baysError && <p className="ego-form__error" role="alert">{baysError}</p>}
        {validationErrors.bayId && (
          <p className="ego-form__error" role="alert">{validationErrors.bayId}</p>
        )}
      </div>

      <div className="ego-form__field">
        <label htmlFor="ego-distance-unit" className="ego-form__label">Distance Unit</label>
        <select
          id="ego-distance-unit"
          className="ego-form__select"
          value={formState.distanceUnit}
          onChange={(e) => setDistanceUnit(e.target.value as DistanceUnit)}
        >
          {DISTANCE_UNIT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className="ego-form__actions">
        <button
          type="submit"
          className="ego-form__save-btn"
          disabled={isSaving}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {saveSuccess && (
        <p className="ego-form__success" role="status">Configuration saved successfully.</p>
      )}
      {saveError && (
        <p className="ego-form__error" role="alert">{saveError}</p>
      )}
    </form>
  );
}
